/**
 * Copyright 2021 GeoComply
 * Author: Hao Nguyen (hao@geocomply.com)
 * 
 * GeoComply React Native Module APIs
 * 
 */


import { NativeModules } from 'react-native';
import { STATUS_SUCCESS, STATUS_FAILED } from './status';
import {
  GEOCOMPLY_GEOLOCATION_AVAILABLE_EVENT,
  GEOCOMPLY_GEOLOCATION_FAILED_EVENT,
  GEOCOMPLY_MYIP_SUCCESS_EVENT,
  GEOCOMPLY_MYIP_FAILED_EVENT,
  GEOCOMPLY_GEOLOCATION_CANCELLATION_FINISHED_EVENT,
  GEOCOMPLY_GEOLOCATION_STOPPED_UPDATING_EVENT,
  GEOCOMPLY_GEOLOCATION_BLUETOOTH_ERROR_EVENT
} from './emit_events';


const geocomply = () => {

  const ReasonCode = {
    LOGIN: "1",
    PRE_WAGER: "2",
    INTERVAL: "3",
    IP_CHANGE: "4",
    USER_DRIVEN_RETRY: "5",
    POST_INTERVAL_FOREGROUND: "6",
    GAME_LAUNCH_OR_SWITCH: "7",
    REGISTRATION: "8",
    ACCOUNT_CHANGES_NAME_ADD: "9a",
    ACCOUNT_CHANGES_NAME_CHANGE: "9b",
    ACCOUNT_CHANGES_NAME_REMOVE: "9c",
    ACCOUNT_CHANGES_PHONE_ADD: "9d",
    ACCOUNT_CHANGES_PHONE_CHANGE: "9e",
    ACCOUNT_CHANGES_PHONE_REMOVE: "9f",
    ACCOUNT_CHANGES_EMAIL_ADD: "9g",
    ACCOUNT_CHANGES_EMAIL_CHANGE: "9h",
    ACCOUNT_CHANGES_EMAIL_REMOVE: "9i",
    ACCOUNT_CHANGES_PASSWORD: "9j",
    ACCOUNT_CHANGES_SECURITY_ADD: "9k",
    ACCOUNT_CHANGES_SECURITY_REMOVE: "9l",
    ACCOUNT_CHANGES_PHYSICAL_ADDRESS_ADD: "9m",
    ACCOUNT_CHANGES_PHYSICAL_ADDRESS_CHANGE: "9n",
    ACCOUNT_CHANGES_PHYSICAL_ADDRESS_REMOVE: "9o",
    ACCOUNT_CHANGES_CONTACTS_ADD: "9p",
    ACCOUNT_CHANGES_CONTACTS_CHANGE: "9q",
    ACCOUNT_CHANGES_CONTACTS_REMOVE: "9r",
    PAYMENT_INFORMATION_ADD_FUNDING_METHOD: "10a",
    PAYMENT_INFORMATION_CHANGE_FUNDING_METHOD: "10b",
    PAYMENT_INFORMATION_REMOVE_FUNDING_METHOD: "10c",
    PAYMENT_INFORMATION_ADD_WITHDRAWAL_METHOD: "10d",
    PAYMENT_INFORMATION_CHANGE_WITHDRAWAL_METHOD: "10e",
    PAYMENT_INFORMATION_REMOVE_WITHDRAWAL_METHOD: "10f",
    TRANSACTION_DEPOSIT: "11a",
    TRANSACTION_WITHDRAWAL: "11b",
    TRANSACTION_PURCHASE_WAGER: "11c",
    TRANSACTION_TRANSFER_SELL: "11d",
    TRANSACTION_TRANSFER_SAME_USER: "11e",
    TRANSACTION_TRANSFER_DIFFERENT_USER: "11f",
    TRANSACTION_NAVIGATION_BALANCE: "12a",
    TRANSACTION_NAVIGATION_TRANSACTION_HISTORY: "12b",
    TRANSACTION_NAVIGATION_TRANSACTION_DETAILS: "12c",
    TRANSACTION_NAVIGATION_PROFILE: "12d",
    TRANSACTION_NAVIGATION_HELP_CENTER: "12e",
    TRANSACTION_NAVIGATION_TERMS_OF_USE: "12f",
    TRANSACTION_NAVIGATION_CONTACTS: "12g"
};
  
  /**
   * onRequestGeolocation:  This method triggers a geolocation process to create a geolocation transaction.
   * @param {Object} values The required JSON which contains mandatory information to perform a geolocation request.
   * @return {Promise}      The Promise that indicates the status after calling this API. Please refer to status.js.
   * 
   * Example of `values`:
   * 
   * const values = {
                      "license": license,
                      "userId": user_id,
                      "reason": geolocation_reason,
                      "cancel_if_ls_disabled": true/false,
                      "phoneNumber": phone_number,
                      "userSessionId": userSessionId,
                      "custom_fields": {
                        "field": value
                      },
                      reason_code: ReasonCode.LOGIN,
                    }
   *
   * Notes:
   * - The operator app must set value for license, userId, reason, and cancel_if_ls_disabled when calling this method.
   * - The license is a text string of ASCII characters up to 2 KB in length.
   * - The license service is used for authorizing access to our geolocation services.
   * - The default configuration for our license expiry is 24 hours.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */

  const onRequestGeoLocation = async (values) => {
    try {
      const response = await NativeModules.RNGeoComplySdkWrapperModule.requestGeolocation(values);
      return { code: STATUS_SUCCESS, log: '' + response };
    } catch (error) {
      return { code: STATUS_FAILED, log: `${error}` };
    }
  };


  /**
   * cancelCurrentGeolocation: The operator app calls this method to cancel the current geolocation request.
   * @param cancelReason       The reason code indicates why the current geolocation request is canceled.
                               Its value must be one of the following values:
                               NO_REASON | USER_CANCELS | APP_ENTER_BACKGROUND | REQUEST_NO_LONGER_NEEDED | REQUEST_TAKES_TOO_LONG | OTHERS  
   * @param cancelMessage      The message indicates more detail about why the current geolocation request is canceled.
   * @return {Promise}         The Promise indicates the status after calling this API. Please refer to status.js.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */

  const onCancelCurrentGeolocation = async (cancelReason, cancelMessage) => {
    try {
      const cancelResponse = await NativeModules.RNGeoComplySdkWrapperModule.cancelCurrentGeolocation(cancelReason, cancelMessage);
      return { code: STATUS_SUCCESS, log: '' + cancelResponse };
    } catch (error) {
      return { code: STATUS_FAILED, log: `${error}` };
    }
  };


  /**
   * isGeolocationInProgress: The operator app calls this method to know whether geolocation is in progress or not.
   * @return {Promise}        The Promise that indicates the status after calling this API. Please refer to status.js.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */

  const onIsGeolocationInProgress = async () => {
    try {
      const isGeolocationInProgress = await NativeModules.RNGeoComplySdkWrapperModule.isGeolocationInProgress();
      return { code: STATUS_SUCCESS, log: '' + isGeolocationInProgress, result: isGeolocationInProgress };
    } catch (error) {
      return { code: STATUS_FAILED, log: `${error}` };
    }
  };


  /**
   * onStartUpdating:       This method triggers the Geolocation process to automatically reschedule the next indoor geolocation updates.
   * @param {Object} values The required JSON which contains mandatory information to perform a geolocation request.
   * @return {Promise}      The Promise that indicates the status after calling this API. Please refer to status.js.
   * 
   * Example of `values`:
   * 
   * const values = {
                      "license": license,
                      "userId": user_id,
                      "reason": geolocation_reason,
                      "cancel_if_ls_disabled": true/false,
                      "phoneNumber": phone_number,
                      "userSessionId": userSessionId,
                      "custom_fields": {
                        "field": value
                      },
                      reason_code: ReasonCode.LOGIN,
                    }
   *
   * Notes:
   * - The operator app must set value for license, userId, reason, and cancel_if_ls_disabled when calling this method.
   * - The license is a text string of ASCII characters up to 2 KB in length.
   * - The license service is used for authorizing access to our geolocation services.
   * - The default configuration for our license expiry is 24 hours.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */

  const onStartUpdating = async (values) => {
    try {
      const response = await NativeModules.RNGeoComplySdkWrapperModule.startUpdating(values);
      return { code: STATUS_SUCCESS, log: '' + response };
    } catch (error) {
      return { code: STATUS_FAILED, log: `${error}` };
    }
  };


  /**
   * onStopUpdating:        The operator app calls this method to stop rescheduling the next indoor geolocation updates.
   * @param {Object} values The required JSON which contains mandatory information to perform a geolocation request.
   * @return {Promise}.     The Promise that indicates the status after calling this API. Please refer to status.js.
   * 
   * Example of `values`:
   * 
   * const values = {
                      "license": license,
                      "userId": user_id
                    }
   *
   * Notes:
   * - The operator app must set value for license and user_id when calling this method.
   * - The license is a text string of ASCII characters up to 2 KB in length.
   * - The license service is used for authorizing access to our geolocation services.
   * - The default configuration for our license expiry is 24 hours.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */

  const onStopUpdating = async (values) => {
    try {
      const response = await NativeModules.RNGeoComplySdkWrapperModule.stopUpdating(values);
      return { code: STATUS_SUCCESS, log: '' + response };
    } catch (error) {
      return { code: STATUS_FAILED, log: `${error}` };
    }
  };


  /**
   * onIsIndoorUpdating: The operator app calls this method to know whether indoor geolocation is in progress or not.
   * @return {Promise}   The Promise that indicates the status after calling this API. Please refer to status.js.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */

  const onIsIndoorUpdating = async () => {
    try {
      const isIsIndoorUpdating = await NativeModules.RNGeoComplySdkWrapperModule.isIndoorUpdating();
      return { code: STATUS_SUCCESS, log: '' + isIsIndoorUpdating, result: isIsIndoorUpdating };
    } catch (error) {
      return { code: STATUS_FAILED, log: `${error}` };
    }
  };


  /**
   * onStartMyIpService:    The operator app calls this method to start MyIP service.
   * @param {Object} values The required JSON which contains mandatory information to perform a geolocation request.
   * @return {Promise}      The Promise that indicates the status after calling this API. Please refer to status.js.
   * 
   * Example of `values`:
   * 
   * const values = {
                      "license": license,
                      "userId": user_id
                    }
   *
   * Notes:
   * - The operator app must set value for license and userId when calling this method.
   * - The license is a text string of ASCII characters up to 2 KB in length.
   * - The license service is used for authorizing access to our geolocation services.
   * - The default configuration for our license expiry is 24 hours.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */

  const onStartMyIpService = async (values) => {
    try {
      const response = await NativeModules.RNGeoComplySdkWrapperModule.startMyIpService(values);
      return { code: STATUS_SUCCESS, log: '' + response };
    } catch (error) {
      return { code: STATUS_FAILED, log: `${error}` };
    }
  };


  /**
   * onStopMyIpService: The operator app calls this method to stop MyIP service.
   * @return {Promise}  The Promise that indicates the status after calling this API. Please refer to status.js.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */

  const onStopMyIpService = async () => {
    try {
      const response = await NativeModules.RNGeoComplySdkWrapperModule.stopMyIpService();
      return { code: STATUS_SUCCESS, log: '' + response };
    } catch (error) {
      return { code: STATUS_FAILED, log: `${error}` };
    }
  };


  /**
   * onAckMyIpSuccess: The operator app calls this method to acknowledge receipt of an IP address change.
   * @return {Promise} The Promise that indicates the status after calling this API. Please refer to status.js.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */

  const onAckMyIpSuccess = async () => {
    try {
      const response = await NativeModules.RNGeoComplySdkWrapperModule.ackMyIpSuccess();
      return { code: STATUS_SUCCESS, log: '' + response };
    } catch (error) {
      return { code: STATUS_FAILED, log: `${error}` };
    }
  };


  /**
   * onIsMyIpServiceRunning: The operator app calls this method to know whether MyIP service is running or not.
   * @return {Promise}       The Promise that indicates the status after calling this API. Please refer to status.js.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */

  const onIsMyIpServiceRunning = async () => {
    try {
      const isMyIpServiceRunning = await NativeModules.RNGeoComplySdkWrapperModule.isMyIpServiceRunning();
      return { code: STATUS_SUCCESS, log: '' + isMyIpServiceRunning, result: isMyIpServiceRunning };
    } catch (error) {
      return { code: STATUS_FAILED, log: `${error}` };
    }
  };


  /**
   * onGetCurrentUserSessionID: The operator app calls this method to get the current user session ID.
   * @return {userSessionId}    The current user session ID.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */

  const onGetCurrentUserSessionID = async () => {
    try {
      const userSessionId = await NativeModules.RNGeoComplySdkWrapperModule.getCurrentUserSessionID();
      return { code: STATUS_SUCCESS, log: '' + userSessionId, result: userSessionId };
    } catch (error) {
      return { code: STATUS_FAILED, log: `${error}` };
    }
  };


  /**
   * onInvalidateUserSession: The operator app calls this method to invalidate the current user session ID.
   * @return {Promise}        The Promise that indicates the status after calling this API. Please refer to status.js.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */

  const onInvalidateUserSession = async () => {
    try {
      const response = await NativeModules.RNGeoComplySdkWrapperModule.invalidateUserSession();
      return { code: STATUS_SUCCESS, log: '' + response };
    } catch (error) {
      return { code: STATUS_FAILED, log: `${error}` };
    }
  };

  /**
   * onGetReasonCode:  The operator app calls this method to get the current reason code.
   * @return {Promise}        The Promise that indicates the status after calling this API. Please refer to status.js.
   * 
   * For further information, please refer to our GeoComply React Native Module Interface Definition Document.
   * 
   */
      const onGetReasonCode = async () => {
        try {
          const reasonCode = await NativeModules.RNGeoComplySdkWrapperModule.getReasonCode();
          return { code: STATUS_SUCCESS, log: '' + reasonCode, result: reasonCode};
        } catch (error) {
          return { code: STATUS_FAILED, log: `${error}` };
        }
      };
    


  return {
    STATUS_SUCCESS,
    STATUS_FAILED,
    GEOCOMPLY_GEOLOCATION_AVAILABLE_EVENT,
    GEOCOMPLY_GEOLOCATION_FAILED_EVENT,
    GEOCOMPLY_MYIP_SUCCESS_EVENT,
    GEOCOMPLY_MYIP_FAILED_EVENT,
    GEOCOMPLY_GEOLOCATION_CANCELLATION_FINISHED_EVENT,
    GEOCOMPLY_GEOLOCATION_STOPPED_UPDATING_EVENT,
    GEOCOMPLY_GEOLOCATION_BLUETOOTH_ERROR_EVENT,
    onGetReasonCode,
    onRequestGeoLocation,
    onCancelCurrentGeolocation,
    onIsGeolocationInProgress,
    onStartUpdating,
    onStopUpdating,
    onIsIndoorUpdating,
    onStartMyIpService,
    onStopMyIpService,
    onAckMyIpSuccess,
    onIsMyIpServiceRunning,
    onGetCurrentUserSessionID,
    onInvalidateUserSession,
    ReasonCode
  };
};


export default geocomply;
